import type { BestTimeDef, SeasonDef } from '@/lib/best-time-pages';
import { getMonthName } from '@/lib/constants';
import { uiFb } from '@/lib/i18n';

interface BestTimeSummaryTableProps {
  seasons: BestTimeDef['seasons'];
  bestMonths: number[];
  lang: string;
}

/** Semantic HTML table for Google featured snippet extraction. */
export function BestTimeSummaryTable({ seasons, bestMonths, lang }: BestTimeSummaryTableProps) {
  // Build month→season lookup (1-indexed months)
  const monthSeason = new Map<number, SeasonDef>();
  for (const s of seasons) {
    for (const m of s.months) monthSeason.set(m, s);
  }

  // Ordered months 1–12
  const rows = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const s = monthSeason.get(month);
    return {
      month,
      name: getMonthName(i, lang),
      season: s?.name ?? '—',
      temp: s?.tempRange ?? '—',
      crowds: s?.crowds ?? '—',
      prices: s?.prices ?? '—',
      verdict: bestMonths.includes(month) ? '★ Best' : (s?.crowds === 'Low' ? 'Budget' : 'Good'),
    };
  });

  return (
    <div className="mb-8 overflow-x-auto">
      <table className="w-full border-collapse text-xs md:text-sm">
        <caption className="sr-only">
          {uiFb('ui.month_by_month_summary', 'Month-by-month travel summary', lang)}
        </caption>
        <thead>
          <tr className="border-b border-border-subtle text-left text-text-secondary">
            <th className="py-2 pr-3 font-medium">{uiFb('ui.month', 'Month', lang)}</th>
            <th className="py-2 pr-3 font-medium">{uiFb('ui.season', 'Season', lang)}</th>
            <th className="py-2 pr-3 font-medium">{uiFb('ui.temperature', 'Temperature', lang)}</th>
            <th className="py-2 pr-3 font-medium">{uiFb('ui.crowds', 'Crowds', lang)}</th>
            <th className="py-2 pr-3 font-medium">{uiFb('ui.prices', 'Prices', lang)}</th>
            <th className="py-2 font-medium">{uiFb('ui.verdict', 'Verdict', lang)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.month}
              className={`border-b border-border-subtle ${r.verdict ? 'bg-surface-card font-medium' : ''}`}
            >
              <td className="py-1.5 pr-3 text-text-primary">{r.name}</td>
              <td className="py-1.5 pr-3 text-text-secondary">{r.season}</td>
              <td className="py-1.5 pr-3 text-text-secondary">{r.temp}</td>
              <td className="py-1.5 pr-3 text-text-secondary">{r.crowds}</td>
              <td className="py-1.5 pr-3 text-text-secondary">{r.prices}</td>
              <td className="py-1.5 text-text-primary">{r.verdict}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
